import {
  Body,
  Button,
  ButtonProps,
  Flex,
  Modal,
  ModalBody,
  ModalHeader,
  Shape,
  TabItem,
  Tabs,
  styled,
} from '@teko/ds-desktop';
import { AttributeGroup } from '@teko/ecommerce';
import { useProductDetailState, useTenantConfig } from 'contexts';
import DOMPurify from 'dompurify';
import { t } from 'i18n';
import dynamic from 'next/dynamic';
import { useRef, useState } from 'react';

const Drawer = dynamic(() =>
  import('components/Drawer').then((mod) => mod.Drawer),
);

interface SpecificationsProps {
  attributes: AttributeGroup[];
}

type ProductDescriptionProps = {
  buttonType?: ButtonProps['variant'];
  tabTitle?: Array<string>;
};

export const ProductDescription: React.FC<ProductDescriptionProps> = (
  props,
) => {
  const {
    buttonType = 'link',
    tabTitle = [t('Thông tin sản phẩm'), t('Mô tả chi tiết')],
  } = props;

  const contentRef = useRef<HTMLDivElement | null>(null);
  const { product } = useProductDetailState();
  const { epicOmni1643 } = useTenantConfig();
  const displayAttributes = (
    product?.attributeGroups as SpecificationsProps['attributes']
  )?.filter((att) => !!att.value && att.parentId !== 0);

  const [isShowMoreAttributes, setShowMoreAttributes] = useState(false);
  const description = product?.seoInfo?.description;
  const [showMoreDescriptionNeeded, setShowMoreDescriptionNeeded] =
    useState(false);
  const [isExpanded, setExpanded] = useState(false);

  const MAX_PRODUCT_DESCRIPTION_HEIGHT = epicOmni1643 ? 632 : 503;
  const MAX_PRODUCT_DESCRIPTION_LINES = epicOmni1643 ? 11 : 8;

  return (
    <WrapperProductDescription
      id="desktop-product-description"
      backgroundColor="white"
      borderRadius="xl"
      padding={['s0', 's16']}
    >
      <StyledTabs
        defaultTab={0}
        type="line"
        textActiveColor="textTitle"
        textColor="textPrimary"
        tabTitleProps={{ style: { padding: 's16' } }}
        width="100%"
      >
        <TabItem minWidth="50%" className="tab-item" text={tabTitle[0]}>
          <SpecificationsWrapper>
            {!!displayAttributes?.length ? (
              <>
                <Specifications
                  attributes={displayAttributes.slice(
                    0,
                    MAX_PRODUCT_DESCRIPTION_LINES,
                  )}
                />
                {!!displayAttributes?.length &&
                  displayAttributes.length > MAX_PRODUCT_DESCRIPTION_LINES && (
                    <StyledButton
                      variant={buttonType}
                      onClick={() => setShowMoreAttributes(true)}
                      margin="s16"
                    >
                      {t('Xem thêm')}
                    </StyledButton>
                  )}
              </>
            ) : (
              <Shape
                height={MAX_PRODUCT_DESCRIPTION_HEIGHT}
                backgroundColor="white"
              ></Shape>
            )}
          </SpecificationsWrapper>
        </TabItem>
        <TabItem minWidth="50%" className="tab-item" text={tabTitle[1]}>
          {!!description ? (
            <Content>
              <Shape
                style={
                  showMoreDescriptionNeeded
                    ? {
                        overflow: 'hidden',
                        transition: 'height 0.3s ease',
                        height: isExpanded
                          ? contentRef.current?.clientHeight
                          : `${MAX_PRODUCT_DESCRIPTION_HEIGHT}px`,
                      }
                    : {}
                }
              >
                <StyledDescription
                  className="ck-content"
                  ref={(e) => {
                    !!e &&
                      !showMoreDescriptionNeeded &&
                      setShowMoreDescriptionNeeded(
                        e.clientHeight > MAX_PRODUCT_DESCRIPTION_HEIGHT,
                      );
                    contentRef.current = e;
                  }}
                  isExpanded={isExpanded}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify?.sanitize?.(description, {
                      ADD_TAGS: ['iframe'],
                    }),
                  }}
                  showMoreNeeded={showMoreDescriptionNeeded}
                />
              </Shape>
              {!!showMoreDescriptionNeeded ? (
                <Flex justify="center" padding="s12">
                  <StyledButton
                    variant={buttonType}
                    onClick={() => setExpanded((previous) => !previous)}
                  >
                    {isExpanded ? t('Thu gọn') : t('Xem thêm')}
                  </StyledButton>
                </Flex>
              ) : null}
            </Content>
          ) : (
            <Shape
              height={MAX_PRODUCT_DESCRIPTION_HEIGHT}
              backgroundColor="white"
            ></Shape>
          )}
        </TabItem>
      </StyledTabs>
      {!epicOmni1643 ? (
        <Modal
          show={isShowMoreAttributes}
          onHide={() => setShowMoreAttributes(false)}
        >
          <ModalHeader>{t('Thông tin sản phẩm')}</ModalHeader>
          <StyledModalBody>
            <Specifications attributes={displayAttributes} />
          </StyledModalBody>
        </Modal>
      ) : (
        <Drawer
          open={isShowMoreAttributes}
          onClose={() => setShowMoreAttributes(false)}
          title={t('Thông tin sản phẩm')}
          content={<Specifications attributes={displayAttributes} />}
        />
      )}
    </WrapperProductDescription>
  );
};

const WrapperProductDescription = styled(Shape)`
  height: 100%;
  min-height: 632px;
`;

const Specifications: React.FC<SpecificationsProps> = ({
  attributes,
}: SpecificationsProps) => {
  return (
    <Shape width="100%">
      {attributes.map((att, index) => {
        return (
          <AttributeWrapper
            key={`${index}${att.name}`}
            direction="row"
            padding={['s12', 's16']}
            gap="s8"
          >
            <Body level={1} color="textSecondary" style={{ flex: 2 }}>
              {att.name}
            </Body>
            <Body
              level={1}
              color="textPrimary"
              dangerouslySetInnerHTML={{
                __html: DOMPurify?.sanitize?.(att.value, {
                  ADD_TAGS: ['iframe'],
                }),
              }}
              style={{ flex: 3 }}
            ></Body>
          </AttributeWrapper>
        );
      })}
    </Shape>
  );
};

const AttributeWrapper = styled(Flex)`
  &:nth-of-type(even) {
    background-color: #f6f6f6;
  }
`;

const SpecificationsWrapper = styled(Flex)`
  > * {
    max-width: 800px;
  }
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyledModalBody = styled(ModalBody)`
  max-height: 85vh;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background: #cccccc;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #c8c5c5;
  }
`;

const StyledDescription = styled.div<{
  isExpanded: boolean;
  showMoreNeeded: boolean;
}>(
  ({
    isExpanded,
    showMoreNeeded,
  }: {
    isExpanded: boolean;
    showMoreNeeded: boolean;
  }) => `
  overflow: hidden;
  * {
    max-width: 100%;
  }
    
  .table table {
    table-layout: auto;
  }
  ul,
  ul li {
    list-style-type: inherit;
  }

  ul {
    /* Default user agent stylesheet. */
    padding-left: 40px;
  }

  ol,
  ol li {
    list-style-type: decimal;
  }
  ol {
    /* Default user agent stylesheet, you can change it to your needs. */
    padding-left: 40px;
  }
  ${
    !isExpanded && showMoreNeeded
      ? `
  &:after {
    content: ' ';
    background: linear-gradient(transparent, hsla(0, 0%, 100%, 0) 70%, #fff 90%);
    background-image: linear-gradient(rgba(255, 255, 255, 0), rgb(255, 255, 255));
    position: absolute;
    left: 0;
    bottom: 56px;
    height: 40px;
    width: 100%;
  };
  padding-bottom: 14px;
  `
      : ''
  }
`,
);

const StyledButton = styled(Button)<{ variant: ButtonProps['variant'] }>`
  max-height: 32px;
  height: 32px;
  border-radius: 24px;
  border: 1px solid
    ${({ theme, variant }) =>
      variant === 'ghost' ? theme.colors.border : 'transparent'};
`;

const Content = styled.div`
  line-height: 1.5rem;
  padding: 16px 0px;
  h2 {
    font-size: 20px;
    line-height: 1.4;
  }
  h3 {
    font-size: 16px;
    line-height: 1.3;
  }
  h4 {
    font-size: 14px;
  }
  h5 {
    font-size: 12px;
  }
  h6 {
    font-size: 10px;
  }
  img {
    height: auto;
  }

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyledTabs = styled(Tabs)`
  .tab-item {
    padding: 16px;
  }
  .tab-item > div:is([type='body'], [type='subtitle']) {
    font-size: 18px;
  }
`;
